/**
 * useBulkSyncGalleries - Sync job details for multiple galleries at once.
 *
 * Fetches galleries needing sync, then calls the worker endpoint for each
 * with progress tracking and error handling.
 *
 * @see /src/features/gallery/hooks/useSyncGalleryImages.ts - Single gallery sync
 * @see /apps/production/jobber-image-processor - Worker that handles sync
 */

import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import type { JobDetailsSyncStatus } from './useJobDetailsSyncStatus'

const WORKER_URL = import.meta.env.VITE_JOBBER_IMAGE_WORKER_URL

/**
 * Delay between sync requests to avoid rate limiting.
 * N8N/Jobber API has rate limits - be conservative.
 * @see Worker uses GraphQL with cost tracking (10k available, 500 restore rate)
 */
const SYNC_DELAY_MS = 2000 // 2 seconds between requests

/** Helper to delay execution */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export interface BulkSyncProgress {
  total: number
  completed: number
  succeeded: number
  failed: number
  current: string | null
}

export interface BulkSyncResult {
  succeeded: number
  failed: number
  errors: Array<{ galleryId: number; title: string; error: string }>
}

/**
 * Gallery info needed for sync
 */
interface GalleryToSync {
  id: number
  title: string
  job_number: number
  franchise_region: string
}

/**
 * Hook for bulk syncing galleries that need job details
 */
export function useBulkSyncGalleries() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState<BulkSyncProgress>({
    total: 0,
    completed: 0,
    succeeded: 0,
    failed: 0,
    current: null,
  })

  /**
   * Fetch galleries that need sync (have job_number but no job details).
   * Uses pagination to avoid Supabase 1000 row limit.
   */
  const fetchGalleriesToSync = useCallback(async (): Promise<GalleryToSync[]> => {
    // Get synced job numbers (small table, no pagination needed)
    const { data: syncedJobs } = await supabase
      .from('jobber_job_details')
      .select('job_number, franchise_region')

    // Build a set of synced job_number + franchise combinations
    const syncedSet = new Set<string>()
    for (const jd of syncedJobs || []) {
      const franchiseVariants = [jd.franchise_region]
      if (jd.franchise_region === 'northyork') franchiseVariants.push('north_york')
      if (jd.franchise_region === 'hamilton') franchiseVariants.push('HQ')
      for (const fr of franchiseVariants) {
        syncedSet.add(`${jd.job_number}|${fr}`)
      }
    }

    // Fetch galleries in batches to avoid 1000 row limit
    const allGalleries: GalleryToSync[] = []
    let offset = 0
    const batchSize = 1000

    while (true) {
      const { data: galleries, error } = await supabase
        .from('galleries')
        .select('id, title, job_number, franchise_region')
        .not('job_number', 'is', null)
        .range(offset, offset + batchSize - 1)

      if (error) throw error
      if (!galleries || galleries.length === 0) break

      // Filter to only those not synced
      for (const g of galleries) {
        const key = `${g.job_number}|${g.franchise_region}`
        if (!syncedSet.has(key)) {
          allGalleries.push(g as GalleryToSync)
        }
      }

      if (galleries.length < batchSize) break
      offset += batchSize
    }

    return allGalleries
  }, [])

  /**
   * Sync a single gallery (internal helper)
   *
   * Note: gallery_job_images.job_id is a FK to franchise_image_jobs.id (integer),
   * NOT the actual Jobber job ID. We need to join or use franchise_image_jobs directly.
   */
  const syncGallery = async (gallery: GalleryToSync): Promise<void> => {
    // Map franchise_region to expected format for worker
    const franchiseMap: Record<string, string> = {
      'north_york': 'northyork',
      'HQ': 'hamilton',
    }
    const franchise = franchiseMap[gallery.franchise_region] ?? gallery.franchise_region

    // First try: Get job_id via gallery_job_images → franchise_image_jobs join
    const { data: linkedJob } = await supabase
      .from('gallery_job_images')
      .select('franchise_image_jobs!inner(job_id)')
      .eq('gallery_id', gallery.id)
      .limit(1)
      .maybeSingle()

    // Extract the Jobber ID from the joined result
    let jobId: string | null = null
    if (linkedJob?.franchise_image_jobs) {
      // Handle both array and single object responses from Supabase
      const fij = Array.isArray(linkedJob.franchise_image_jobs)
        ? linkedJob.franchise_image_jobs[0]
        : linkedJob.franchise_image_jobs
      jobId = fij?.job_id || null
    }

    // Fallback: Try franchise_image_jobs directly by job_number AND franchise_region
    if (!jobId) {
      // Map gallery franchise_region to franchise_image_jobs format
      const fijFranchise = gallery.franchise_region === 'HQ' ? 'HQ' : gallery.franchise_region

      const { data: franchiseImage } = await supabase
        .from('franchise_image_jobs')
        .select('job_id')
        .eq('job_number', gallery.job_number)
        .eq('franchise_region', fijFranchise)
        .limit(1)
        .maybeSingle()

      jobId = franchiseImage?.job_id || null
    }

    if (!jobId) {
      throw new Error(`No Jobber job_id found for job #${gallery.job_number}. Images may not be synced yet.`)
    }

    // Call worker with the actual Jobber job_id
    const response = await fetch(`${WORKER_URL}/sync-job-images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId, franchise })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Sync failed: ${response.status} - ${errorText}`)
    }

    // After successful sync, fetch the job details and update gallery_jobber_details
    // This ensures the "Enriched" badge shows correctly in the UI
    const { data: jobDetails } = await supabase
      .from('jobber_job_details')
      .select('*')
      .eq('job_number', gallery.job_number)
      .maybeSingle()

    if (jobDetails) {
      // Extract client data from JSONB
      const clientData = jobDetails.client_data as Record<string, unknown> | null
      const propertyData = jobDetails.property_data as Record<string, unknown> | null
      const addressData = propertyData?.address as Record<string, unknown> | null

      await supabase
        .from('gallery_jobber_details')
        .upsert({
          gallery_id: gallery.id,
          job_id: jobId,
          job_number: gallery.job_number,
          franchise_region: gallery.franchise_region,
          enrichment_status: 'enriched',
          fetched_on: new Date().toISOString(),
          // Client info
          client_name: clientData
            ? `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim()
            : null,
          client_company: clientData?.companyName as string || null,
          client_email: Array.isArray(clientData?.emails)
            ? (clientData.emails[0] as Record<string, unknown>)?.address as string
            : null,
          client_phone: Array.isArray(clientData?.phones)
            ? (clientData.phones[0] as Record<string, unknown>)?.number as string
            : null,
          // Property info
          service_address: addressData?.street1 as string || null,
          service_city: addressData?.city as string || null,
          service_province: addressData?.province as string || null,
          service_postal_code: addressData?.postalCode as string || null,
          // Job info
          job_status: jobDetails.job_status,
          job_total: jobDetails.total_amount,
          line_items: jobDetails.line_items,
          visits: jobDetails.visits,
          jobber_web_uri: jobDetails.jobber_web_uri,
        }, { onConflict: 'gallery_id' })
    }
  }

  /**
   * Run bulk sync for all galleries needing sync
   */
  const runBulkSync = useCallback(async (): Promise<BulkSyncResult> => {
    if (!WORKER_URL) {
      toast({
        title: 'Configuration error',
        description: 'VITE_JOBBER_IMAGE_WORKER_URL is not configured',
        variant: 'destructive',
      })
      return { succeeded: 0, failed: 0, errors: [] }
    }

    setIsRunning(true)
    const errors: BulkSyncResult['errors'] = []

    try {
      const galleries = await fetchGalleriesToSync()

      if (galleries.length === 0) {
        toast({
          title: 'Nothing to sync',
          description: 'All galleries with job numbers are already synced.',
        })
        return { succeeded: 0, failed: 0, errors: [] }
      }

      setProgress({
        total: galleries.length,
        completed: 0,
        succeeded: 0,
        failed: 0,
        current: null,
      })

      let succeeded = 0
      let failed = 0

      for (const gallery of galleries) {
        setProgress(p => ({ ...p, current: gallery.title }))

        let syncSuccess = false
        try {
          await syncGallery(gallery)
          succeeded++
          syncSuccess = true
        } catch (error) {
          failed++
          errors.push({
            galleryId: gallery.id,
            title: gallery.title,
            error: error instanceof Error ? error.message : 'Unknown error',
          })
        }

        setProgress(p => ({
          ...p,
          completed: p.completed + 1,
          succeeded,
          failed,
        }))

        // Update sync status cache directly for immediate UI feedback
        // Only increment synced count when sync actually succeeds
        if (syncSuccess) {
          queryClient.setQueryData<JobDetailsSyncStatus>(
            ['job-details-sync-status'],
            (old) => {
              if (!old) return old
              return {
                ...old,
                synced: old.synced + 1,
                notSynced: Math.max(0, old.notSynced - 1),
              }
            }
          )
        }

        // Rate limit: wait before next request (skip on last item)
        if (galleries.indexOf(gallery) < galleries.length - 1) {
          await delay(SYNC_DELAY_MS)
        }
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['job-details-sync-status'] })
      queryClient.invalidateQueries({ queryKey: ['jobber-job-details'] })
      queryClient.invalidateQueries({ queryKey: ['gallery-jobber-details'] })
      queryClient.invalidateQueries({ queryKey: ['galleries'] })

      // Show completion toast
      if (failed === 0) {
        toast({
          title: 'Bulk sync complete',
          description: `Successfully synced ${succeeded} galleries.`,
        })
      } else {
        toast({
          title: 'Bulk sync completed with errors',
          description: `Synced ${succeeded} galleries, ${failed} failed.`,
          variant: 'destructive',
        })
      }

      return { succeeded, failed, errors }
    } finally {
      setIsRunning(false)
      setProgress(p => ({ ...p, current: null }))
    }
  }, [fetchGalleriesToSync, queryClient, toast])

  return {
    runBulkSync,
    isRunning,
    progress,
  }
}

export default useBulkSyncGalleries
